import { useEffect, useState, useMemo } from 'react';
import Checkbox from '../../../../Checkbox/Checkbox';
import {
  ArrayFilterKeys,
  FilterState,
  useProductsFilter,
} from '../../../../../Context/ProductsFilterContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import styles from './FilterOption.module.scss?module';
import clsx from 'clsx';

export type SubFilterNodeProps = {
  name: string;
  value: number | string;
  count: number;
  filterKey: keyof FilterState;
};

type MultiFilterOptionProps = {
  mode?: 'multi';
  value: string | number;
  name: string | number;
  count: string | number;
  filterKey: keyof Omit<FilterState, 'sortBy' | 'page' | 'view'>;
  node?: SubFilterNodeProps[];
  onClick?: () => void;
};

type SingleFilterOptionProps = {
  mode: 'single';
  value: string | number;
  name: string | number;
  count?: string | number;
  filterKey: 'sortBy';
  node?: never;
  onClick?: never;
};

export type FilterOptionProps = MultiFilterOptionProps | SingleFilterOptionProps;

const FilterOption = (props: FilterOptionProps) => {
  const isSingle = props.mode === 'single';
  const { value, name, filterKey } = props;
  const count = props.count;
  const node = !isSingle ? props.node : undefined;
  const { filters, toggleArrayFilter, updateFilters, setSingleFilter } = useProductsFilter();
  const [isChecked, setChecked] = useState(false);
  const [showSubFilters, setShowSubFilters] = useState(false);

  const hasSubFilters = !isSingle && node && node.length > 0;

  useEffect(() => {
    if (isSingle) {
      setChecked(filters.sortBy === value.toString());
      return;
    }
    const filterValue = value.toString();
    const filterArray = filters[filterKey as ArrayFilterKeys] as string[];
    const isSelected = Array.isArray(filterArray) && filterArray.includes(filterValue);
    setChecked(isSelected);
  }, [filters, value, filterKey, isSingle]);

  const selectedChildren = useMemo(() => {
    if (isSingle || !node || node.length === 0) return [];
    return node.filter((child) => {
      const childValues = filters[child.filterKey];
      return (
        Array.isArray(childValues) &&
        childValues.includes(child.value.toString())
      );
    });
  }, [filters, node, isSingle]);

  const isIndeterminate = selectedChildren.length > 0;

  const handleToggle = () => {
    if (isSingle) {
      setSingleFilter('sortBy', isChecked ? null : value);
      return;
    }

    if (isIndeterminate) {
      const childMap = new Map<ArrayFilterKeys, string[]>();
      const activeFilterUpdates: Partial<
        Record<ArrayFilterKeys, { value: string; label: string }[]>
      > = {};

      selectedChildren.forEach((child) => {
        const k = child.filterKey as ArrayFilterKeys;
        if (!childMap.has(k)) {
          const currentValues = (filters[k] as string[]) || [];
          childMap.set(k, [...currentValues]);
        }

        const list = childMap.get(k)!;
        const idx = list.indexOf(child.value.toString());
        if (idx > -1) {
          list.splice(idx, 1);
        }
      });

      const updates: Partial<FilterState> = {};
      childMap.forEach((newValues, key) => {
        updates[key] = newValues;
        activeFilterUpdates[key] = newValues.map((v) => {
          const childNode = node?.find(
            (n) => n.filterKey === key && n.value.toString() === v
          );
          return { value: v, label: childNode?.name || v };
        });
      });

      updateFilters(updates, activeFilterUpdates);
      return;
    }

    if (props.onClick) {
      props.onClick();
    } else {
      toggleArrayFilter(filterKey as ArrayFilterKeys, value, name.toString());
    }
  };

  const handleChildClick = (
    childFilterKey: keyof FilterState,
    childValue: string | number,
    childName: string
  ) => {
    const cKey = childFilterKey as ArrayFilterKeys;
    const cValStr = childValue.toString();
    const currentChildValues = (filters[cKey] as string[]) || [];

    const isChildSelected = currentChildValues.includes(cValStr);
    let newChildValues: string[];
    let activeFilterLabels: { value: string; label: string }[];

    if (isChildSelected) {
      newChildValues = currentChildValues.filter((v) => v !== cValStr);
      activeFilterLabels = newChildValues.map((v) => {
        const childNode = node?.find(
          (n) => n.filterKey === cKey && n.value.toString() === v
        );
        return { value: v, label: childNode?.name || v };
      });
    } else {
      newChildValues = [...currentChildValues, cValStr];
      activeFilterLabels = newChildValues.map((v) => {
        if (v === cValStr) {
          return { value: v, label: childName };
        }
        const childNode = node?.find(
          (n) => n.filterKey === cKey && n.value.toString() === v
        );
        return { value: v, label: childNode?.name || v };
      });
    }

    const updates: Partial<FilterState> = {
      [cKey]: newChildValues,
    };

    const activeFilterUpdates: Partial<
      Record<ArrayFilterKeys, { value: string; label: string }[]>
    > = {
      [cKey]: activeFilterLabels,
    };

    if (!isChildSelected && isChecked) {
      const pKey = filterKey as ArrayFilterKeys;
      const pValStr = value.toString();
      const currentParentValues = (filters[pKey] as string[]) || [];

      if (currentParentValues.includes(pValStr)) {
        const newParentValues = currentParentValues.filter(
          (v) => v !== pValStr
        );
        updates[pKey] = newParentValues;
        activeFilterUpdates[pKey] = newParentValues.map((v) => ({
          value: v,
          label: v,
        }));
      }
    }

    updateFilters(updates, activeFilterUpdates);
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="d-flex align-items-center" style={{ height: '20px' }}>
          <Checkbox
            checked={isIndeterminate ? 'indeterminate' : isChecked}
            onClick={handleToggle}
            id={`${filterKey}-${value}-${name}`}
            className={styles.Checkbox}
            label={name.toString()}
            labelClassName={styles.LabelClassName}
          />

          {hasSubFilters ? (
            <div
              className={styles.SubFiltersArrow}
              onClick={() => setShowSubFilters(!showSubFilters)}
            >
              <FontAwesomeIcon
                icon={showSubFilters ? faChevronUp : faChevronDown}
                size="2xs"
              />
            </div>
          ) : null}
        </div>

        {count !== undefined && <div className={styles.Count}>{count}</div>}
      </div>

      {hasSubFilters && (
        <div
          className={clsx(
            styles.SubFiltersWrapper,
            showSubFilters ? styles.open : ''
          )}
        >
          <div className={styles.SubFiltersInner}>
            {node.map(({ name, value, filterKey, count }) => (
              <div className="ps-3 pt-2" key={`${name}-${filterKey}`}>
                <FilterOption
                  name={name}
                  value={value}
                  filterKey={filterKey as any}
                  count={count}
                  onClick={() => handleChildClick(filterKey, value, name)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default FilterOption;
